/**
 * CIPAA Public Vote Page
 *
 * Public page for voting in CIPAA elections (no authentication required).
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  HowToVote as VoteIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  PublicElectionInfo,
  PublicCandidateInfo,
  CipaaStatus,
  formatCPF,
  removeCPFMask,
  formatDateTime,
  CIPAA_STATUS_LABELS,
} from '../../types/cipaa';
import { cipaaService } from '../../services/cipaaService';

const steps = ['Identificação', 'Seleção', 'Confirmação'];

const CipaaPublicVotePage: React.FC = () => {
  const { key } = useParams<{ key: string }>();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Election data
  const [election, setElection] = useState<PublicElectionInfo | null>(null);
  const [candidates, setCandidates] = useState<PublicCandidateInfo[]>([]);

  // Voter data
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [checkingCpf, setCheckingCpf] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  // Voting
  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);

  useEffect(() => {
    if (key) {
      loadElection();
    }
  }, [key]);

  const loadElection = async () => {
    setLoading(true);
    setError(null);
    try {
      const electionData = await cipaaService.getElectionByKey(key!);
      setElection(electionData);

      if (electionData.status === CipaaStatus.VOTING_OPEN) {
        const candidatesData = await cipaaService.getCandidatesForVoting(key!);
        setCandidates(candidatesData);
      }
    } catch (err: any) {
      setError(err.message ?? 'Eleição não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatCPF(value.replace(/\D/g, '').substring(0, 11));
    setCpf(formatted);
    setCpfError(null);
  };

  const validateCpf = async () => {
    const cleanCpf = removeCPFMask(cpf);
    if (cleanCpf.length !== 11) {
      setCpfError('CPF deve ter 11 dígitos');
      return;
    }

    setCheckingCpf(true);
    setCpfError(null);

    try {
      const result = await cipaaService.checkVoted(key!, cleanCpf);
      if (result.hasVoted) {
        setHasVoted(true);
        setCpfError('Você já votou nesta eleição');
      } else {
        setActiveStep(1);
      }
    } catch (err: any) {
      setCpfError(err.message ?? 'Erro ao verificar CPF');
    } finally {
      setCheckingCpf(false);
    }
  };

  const handleSelectCandidate = () => {
    if (selectedCandidate !== null) {
      setActiveStep(2);
    }
  };

  const handleVote = async () => {
    if (!selectedCandidate) return;

    setSubmitting(true);
    setError(null);

    try {
      await cipaaService.registerVote({
        electionKey: key!,
        candidateId: selectedCandidate,
        voterCpf: removeCPFMask(cpf),
      });
      setVoteSuccess(true);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao registrar voto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 'md', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error && !election) {
    return (
      <Box sx={{ maxWidth: 'md', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        <Alert severity="error" icon={<WarningIcon />}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!election) {
    return (
      <Box sx={{ maxWidth: 'md', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        <Alert severity="error">Eleição não encontrada</Alert>
      </Box>
    );
  }

  if (election.status !== CipaaStatus.VOTING_OPEN) {
    return (
      <Box sx={{ maxWidth: 'md', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        <Card>
          <CardHeader
            title={election.name}
            subheader={election.companyName}
          />
          <CardContent>
            <Alert severity="warning" icon={<WarningIcon />}>
              Esta eleição não está aberta para votação.
              <br />
              Status atual: {CIPAA_STATUS_LABELS[election.status]}
            </Alert>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Período de votação: {formatDateTime(election.startDate, election.startTime)} -{' '}
                {formatDateTime(election.endDate, election.endTime)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (voteSuccess) {
    return (
      <Box sx={{ maxWidth: 'md', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SuccessIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Voto Registrado!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Seu voto foi registrado com sucesso na eleição {election.name}.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Ele é secreto: o sistema não guarda a ligação entre o seu nome e o seu voto.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Obrigado por participar!
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 'md', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
      <Card>
        <CardHeader
          title={election.name}
          subheader={election.companyName}
          avatar={<VoteIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Votação aberta até {formatDateTime(election.endDate, election.endTime)}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 0: CPF Identification */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Informe seu CPF
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Digite seu CPF para verificar se você já votou nesta eleição.
              </Typography>

              <TextField
                label="CPF"
                value={cpf}
                onChange={handleCpfChange}
                error={!!cpfError}
                helperText={cpfError}
                fullWidth
                placeholder="000.000.000-00"
                sx={{ mb: 3 }}
                disabled={checkingCpf}
              />

              {hasVoted && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Você já registrou seu voto nesta eleição.
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={validateCpf}
                disabled={cpf.length < 14 || checkingCpf || hasVoted}
                fullWidth
                size="large"
              >
                {checkingCpf ? 'Verificando...' : 'Continuar'}
              </Button>
            </Box>
          )}

          {/* Step 1: Select Candidate */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Selecione seu candidato
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Vagas: {election.effectiveMembers} titulares e {election.alternateMembers} suplentes
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedCandidate}
                  onChange={(e) => setSelectedCandidate(parseInt(e.target.value, 10))}
                >
                  <Grid container spacing={2}>
                    {candidates.map((candidate) => (
                      <Grid
                        key={candidate.id}
                        size={{
                          xs: 12,
                          sm: 6
                        }}>
                        <Paper
                          elevation={selectedCandidate === candidate.id ? 4 : 1}
                          sx={{
                            p: 2,
                            border: 2,
                            borderColor:
                              selectedCandidate === candidate.id
                                ? 'primary.main'
                                : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.light',
                            },
                          }}
                          onClick={() => setSelectedCandidate(candidate.id)}
                        >
                          <FormControlLabel
                            value={candidate.id}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1" fontWeight="medium">
                                  {candidate.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {candidate.occupation ?? 'Sem cargo'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {candidate.department ?? 'Sem setor'}
                                </Typography>
                              </Box>
                            }
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                <Button variant="outlined" onClick={handleBack}>
                  Voltar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSelectCandidate}
                  disabled={selectedCandidate === null}
                  fullWidth
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Confirmation */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirme seu voto
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                Após confirmar, seu voto não poderá ser alterado.
                <br />
                O voto é secreto: o sistema guarda que você votou, mas não guarda em quem.
              </Alert>

              <Paper sx={{ p: 3, mb: 3, bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary">
                  Você está votando em:
                </Typography>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  {candidates.find((c) => c.id === selectedCandidate)?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {candidates.find((c) => c.id === selectedCandidate)?.occupation}
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={handleBack} disabled={submitting}>
                  Voltar
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleVote}
                  disabled={submitting}
                  fullWidth
                  size="large"
                  startIcon={<VoteIcon />}
                >
                  {submitting ? 'Registrando...' : 'Confirmar Voto'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CipaaPublicVotePage;
